<script setup lang="ts">
  import { getInputAriaDescribedBy } from "@/lib/inputComponentUtils"
  import { cn } from "@/lib/utils"
  import { useVModel } from "@vueuse/core"
  import {
    CheckboxIndicator,
    CheckboxRoot,
    useForwardPropsEmits,
    type CheckboxRootProps,
  } from "reka-ui"
  import { computed, type HTMLAttributes } from "vue"
  import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
  } from "@/components/ui/form"
  import Icon from "@/components/ui/icons/Icon.vue"

  defineOptions({
    inheritAttrs: false,
  })
  const props = defineProps<
    Omit<CheckboxRootProps, "modelValue"> & {
      checked?: CheckboxRootProps["modelValue"]
      class?: HTMLAttributes["class"]
      label?: string
      name: string
      extraLabel?: string
      forceError?: boolean
      ariaDescribedby?: string
    }
  >()
  // reka-ui's own update:modelValue type includes "indeterminate", but that state is only ever
  // set programmatically via the checked prop, never emitted from a user interaction.
  const emits = defineEmits<{
    "update:checked": [value: boolean]
  }>()

  const modelValue = useVModel(props, "checked", emits, {
    passive: true,
  })

  const delegatedProps = computed(() => {
    const { class: _, ariaDescribedby: __, checked: ___, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const resolveAriaDescribedby = (
    formDescriptionId: string,
    formMessageId: string,
    error?: string,
  ) =>
    [
      getInputAriaDescribedBy(
        { extraLabel: props.extraLabel, error },
        formDescriptionId,
        formMessageId,
      ),
      props.ariaDescribedby,
    ]
      .filter(Boolean)
      .join(" ") || undefined
</script>

<template>
  <FormField
    v-slot="{ value, handleChange }"
    v-model="modelValue"
    type="checkbox"
    :name="name"
  >
    <FormItem
      v-bind="$attrs"
      :class="
        cn(
          'grid grid-cols-[auto,1fr] items-center gap-x-2 space-y-0 text-left',
          props.class,
        )
      "
    >
      <FormControl
        v-slot="{
          error: formError,
          formItemId,
          ariaInvalid,
          formDescriptionId,
          formMessageId,
        }"
      >
        <CheckboxRoot
          v-bind="forwarded"
          :id="formItemId"
          :model-value="value"
          :aria-invalid="ariaInvalid"
          :aria-describedby="
            resolveAriaDescribedby(formDescriptionId, formMessageId, formError)
          "
          :class="
            cn(
              'peer my-1 h-5 w-5 shrink-0 self-start rounded-xs border-[1.25px] border-secondary-300 bg-secondary-100 text-secondary-700 ring-offset-background',
              'focus-visible:border-secondary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-0',
              'disabled:cursor-not-allowed disabled:border-secondary-400 disabled:bg-secondary-300 disabled:text-secondary-400',
              'data-[state=checked]:text-primary-foreground data-[state=checked]:enabled:border-primary-300 data-[state=checked]:enabled:bg-primary-300',
              'dark:border-secondary-400 dark:bg-secondary-500',
              'dark:data-[state=checked]:enabled:bg-primary-300',
              'dark:focus-visible:border-secondary-500 dark:focus-visible:outline-none dark:focus-visible:ring-primary-300',
              'dark:disabled:border-secondary-500 dark:disabled:bg-secondary-500 dark:disabled:text-secondary-400',
              (formError || props.forceError) &&
                'border-secondary-100 ring-2 ring-red-400 dark:border-secondary-500 dark:ring-red-300',
            )
          "
          @update:model-value="handleChange"
        >
          <CheckboxIndicator class="flex items-center justify-center">
            <Icon
              class="stroke-current"
              size="md"
              variant="secondary"
              name="Check"
            />
          </CheckboxIndicator>
        </CheckboxRoot>
        <FormLabel :class="cn('text-base', !value && 'font-normal')">
          <slot name="label">
            {{ label }}
          </slot>
        </FormLabel>
        <FormDescription
          :class="cn('col-start-2 ')"
          :data-testid="`${name}-checkbox-extra-label`"
        >
          {{ extraLabel }}
        </FormDescription>
        <FormMessage class="col-start-2 pl-0 pt-2" />
      </FormControl>
    </FormItem>
  </FormField>
</template>
